import { useCallback, useEffect, useRef } from 'react';
import { ASSET_LIBRARY_LONG_PRESS_MS } from '../constants';

export type UseAssetLibraryItemGesturesMobileParams = {
    /** When true, tap opens and long-press selects (e.g. folders). Default: tap selects, long-press opens. */
    isAutoOpenOnTap?: boolean;
    onOpenItem: () => void;
    onTap: () => void;
};

export const useAssetLibraryItemGesturesMobile = ({
    isAutoOpenOnTap = false,
    onOpenItem,
    onTap,
}: UseAssetLibraryItemGesturesMobileParams): {
    onClick: () => void;
    onPointerCancel: () => void;
    onPointerDown: () => void;
    onPointerLeave: () => void;
    onPointerUp: () => void;
} => {
    const longPressTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const suppressNextClickRef = useRef(false);

    const clearLongPressTimer = useCallback(() => {
        if (longPressTimerRef.current !== null) {
            clearTimeout(longPressTimerRef.current);
            longPressTimerRef.current = null;
        }
    }, []);

    const longPressAction = isAutoOpenOnTap ? onTap : onOpenItem;
    const tapAction = isAutoOpenOnTap ? onOpenItem : onTap;

    const onPointerDown = useCallback(() => {
        clearLongPressTimer();
        longPressTimerRef.current = setTimeout(() => {
            longPressTimerRef.current = null;
            suppressNextClickRef.current = true;
            longPressAction();
        }, ASSET_LIBRARY_LONG_PRESS_MS);
    }, [clearLongPressTimer, longPressAction]);

    const onPressEnd = useCallback(() => {
        clearLongPressTimer();
    }, [clearLongPressTimer]);

    const onClick = useCallback(() => {
        if (suppressNextClickRef.current) {
            suppressNextClickRef.current = false;
            return;
        }
        tapAction();
    }, [tapAction]);

    useEffect(
        () => () => {
            clearLongPressTimer();
        },
        [clearLongPressTimer],
    );

    return {
        onClick,
        onPointerCancel: onPressEnd,
        onPointerDown,
        onPointerLeave: onPressEnd,
        onPointerUp: onPressEnd,
    };
};
